<template>
  <main class="deb-page">
    <div class="deb-logo-bar">
      <img
        v-if="!logoFailed"
        :src="logoUrl"
        alt="Chas Everitt Luxury"
        @error="logoFailed = true"
      >
      <strong v-else>CHAS EVERITT LUXURY</strong>
    </div>

    <section class="deb-banner">
      <div class="container text-center">
        <h1>Dîner en Blanc</h1>
        <p class="deb-banner__venue">The First Fairway, Dainfern Golf Estate</p>
        <div class="deb-event-details">
          <span><strong>Saturday</strong> 12 September 2026</span>
          <span><strong>17:00</strong> Registration &amp; drinks</span>
          <span><strong>18:00</strong> Dinner served</span>
          <span><strong>R950</strong> per person</span>
        </div>
      </div>
    </section>

    <section class="deb-upload-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-xl-8 col-lg-9">
            <div class="text-center deb-section-heading">
              <h2>Share your photos</h2>
              <p>
                Add your photographs from the evening. All uploads are reviewed
                before appearing in the gallery.
              </p>
            </div>

            <div class="deb-instructions">
              <p><strong>How it works:</strong> Enter your email, choose your photos and submit them for review.</p>
              <p><strong>Returning later?</strong> Enter the same email and select “Manage my photos” to view or delete your uploads.</p>
            </div>

            <form class="deb-upload-panel" @submit.prevent="submitUpload">
              <div class="mb-4">
                <label class="form-label" for="deb-email">Email address</label>
                <div class="deb-email-row">
                  <input
                    id="deb-email"
                    v-model.trim="email"
                    class="form-control"
                    type="email"
                    inputmode="email"
                    autocomplete="email"
                    placeholder="you@example.com"
                    maxlength="254"
                    required
                  >
                  <button
                    class="btn deb-manage-button"
                    type="button"
                    :disabled="!validEmail || managing"
                    @click="managePhotosByEmail"
                  >
                    {{ managing ? 'Opening…' : 'Manage my photos' }}
                  </button>
                </div>
                <small>
                  Used only to identify your uploads and allow you to remove them.
                  Your email address is not displayed or stored in readable form.
                </small>
              </div>

              <input
                ref="fileInput"
                class="deb-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                @change="handleFiles"
              >

              <button
                class="deb-file-picker"
                type="button"
                :disabled="uploading"
                @click="$refs.fileInput.click()"
              >
                <span class="deb-file-picker__icon" aria-hidden="true">+</span>
                <strong>
                  {{ selectedFiles.length ? 'Choose different photos' : 'Choose photos to upload' }}
                </strong>
                <small>Up to 10 images · 15 MB each</small>
              </button>

              <div v-if="selectedFiles.length" class="deb-selected-list">
                <div v-for="(file, index) in selectedFiles" :key="file.key" class="deb-selected-item">
                  <img :src="file.preview" alt="Selected upload preview">
                  <div>
                    <strong>{{ file.name }}</strong>
                    <small>{{ formatFileSize(file.size) }}</small>
                  </div>
                  <button type="button" aria-label="Remove this photo" @click="removeSelected(index)">×</button>
                </div>
              </div>

              <div v-if="uploading" class="deb-progress" aria-live="polite">
                <div><span :style="{ width: uploadProgress + '%' }"></span></div>
                <small>{{ uploadProgress }}% uploaded</small>
              </div>

              <div class="deb-terms-check">
                <input id="deb-terms-accepted" v-model="termsAccepted" type="checkbox">
                <label for="deb-terms-accepted">
                  I have read and agree to the photo gallery terms and conditions.
                </label>
              </div>

              <details class="deb-terms">
                <summary>Photo gallery terms and conditions</summary>
                <ul>
                  <li>All photos are moderated. The event organisers have final discretion to approve, reject, hide or remove any upload at any time.</li>
                  <li>You confirm that you own the photo or have permission to share it, including the consent of recognisable people shown.</li>
                  <li>Do not upload unlawful, offensive, discriminatory, obscene, unsafe, infringing or promotional content.</li>
                  <li>Approved photos may be displayed in this Dîner en Blanc event gallery. Submission does not guarantee publication or transfer your ownership.</li>
                  <li>Your email is used only to identify and manage your uploads. It is converted to a one-way keyed hash and is never displayed.</li>
                  <li>You may delete your uploads here, although copies already viewed, shared or cached elsewhere cannot always be recalled.</li>
                </ul>
              </details>

              <div class="text-center">
                <button class="btn deb-submit-button" type="submit" :disabled="!canUpload">
                  {{ uploading ? 'Uploading…' : 'Submit for review' }}
                </button>
              </div>
            </form>

            <div
              v-if="notice.message"
              class="deb-notice"
              :class="'deb-notice--' + notice.type"
              role="status"
            >
              <strong>{{ notice.type === 'success' ? 'Thank you.' : 'Please note.' }}</strong>
              <span>{{ notice.message }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="ownPhotos.length" ref="ownPhotos" class="deb-own-section">
      <div class="container">
        <div class="deb-section-heading">
          <h2>Your uploads</h2>
          <p>You can remove your own pending or published photographs here.</p>
        </div>

        <div class="row g-3">
          <div
            v-for="photo in ownPhotos"
            :key="photo.id"
            class="col-6 col-md-4 col-lg-3"
          >
            <article class="deb-photo-card">
              <img :src="photo.thumbnail_url" alt="Your Dîner en Blanc upload" loading="lazy">
              <div>
                <span class="deb-status" :class="'deb-status--' + photo.status">
                  {{ statusLabel(photo.status) }}
                </span>
                <button
                  type="button"
                  :disabled="busyId === photo.id"
                  @click="deleteOwn(photo)"
                >
                  {{ busyId === photo.id ? 'Removing…' : 'Delete photo' }}
                </button>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section v-if="adminMode" class="deb-admin-section">
      <div class="container">
        <div class="deb-admin-heading">
          <div>
            <span>Private moderation</span>
            <h2>Review uploads</h2>
          </div>
          <button class="deb-logout" type="button" @click="adminLogout">Leave moderation</button>
        </div>

        <div class="deb-admin-filters">
          <button
            v-for="filter in adminFilters"
            :key="filter.value"
            type="button"
            :class="{ active: adminFilter === filter.value }"
            @click="adminFilter = filter.value"
          >
            {{ filter.label }} ({{ adminCount(filter.value) }})
          </button>
        </div>

        <div v-if="filteredAdminPhotos.length" class="row g-3">
          <div
            v-for="photo in filteredAdminPhotos"
            :key="photo.id"
            class="col-6 col-md-4 col-lg-3"
          >
            <article class="deb-photo-card deb-photo-card--admin">
              <button type="button" class="deb-photo-card__image" @click="openLightbox(photo)">
                <img :src="photo.thumbnail_url" alt="Photograph for moderation" loading="lazy">
              </button>
              <div>
                <span class="deb-status" :class="'deb-status--' + photo.status">
                  {{ statusLabel(photo.status) }}
                </span>
                <div class="deb-admin-actions">
                  <button
                    v-if="photo.status !== 'approved'"
                    type="button"
                    :disabled="busyId === photo.id"
                    @click="moderate(photo, 'approved')"
                  >Approve</button>
                  <button
                    v-if="photo.status !== 'rejected'"
                    type="button"
                    :disabled="busyId === photo.id"
                    @click="moderate(photo, 'rejected')"
                  >Reject</button>
                  <button
                    type="button"
                    class="delete"
                    :disabled="busyId === photo.id"
                    @click="deleteAdmin(photo)"
                  >Delete</button>
                </div>
              </div>
            </article>
          </div>
        </div>
        <p v-else class="deb-empty">There are no photographs in this view.</p>
      </div>
    </section>

    <section class="deb-gallery-section">
      <div class="container">
        <div class="text-center deb-section-heading">
          <h2>Event gallery</h2>
          <p v-if="!galleryLoading && !galleryPhotos.length">
            The gallery is waiting for its first approved photographs.
          </p>
        </div>

        <div v-if="galleryLoading" class="row g-3">
          <div v-for="n in 6" :key="n" class="col-6 col-md-4">
            <span class="deb-loading-tile"></span>
          </div>
        </div>

        <div v-else-if="galleryPhotos.length" class="deb-gallery-grid">
          <button
            v-for="photo in galleryPhotos"
            :key="photo.id"
            type="button"
            @click="openLightbox(photo)"
          >
            <img :src="photo.thumbnail_url" alt="Dîner en Blanc event photograph" loading="lazy">
          </button>
        </div>
      </div>
    </section>

    <div class="deb-sponsors">
      Proudly sponsored by Investec · Fidelity Services Group · Chas Everitt
    </div>

    <div
      v-if="lightboxPhoto"
      class="deb-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Event photograph"
      @click.self="closeLightbox"
    >
      <button type="button" aria-label="Close photograph" @click="closeLightbox">×</button>
      <img :src="lightboxPhoto.image_url" alt="Dîner en Blanc event photograph">
    </div>
  </main>
</template>

<script>
export default {
  name: 'DinerEnBlanc',

  props: {
    apiUrl: {
      type: String,
      default: 'https://luxury.chaseveritt.co.za/api/upload.php'
    },
    logoUrl: {
      type: String,
      default: '/images/luxury-portfolio.png'
    }
  },

  data: function () {
    return {
      logoFailed: false,
      email: '',
      selectedFiles: [],
      uploading: false,
      uploadProgress: 0,
      termsAccepted: false,
      notice: { type: '', message: '' },
      managing: false,
      managementToken: '',
      galleryPhotos: [],
      galleryLoading: true,
      ownPhotos: [],
      adminMode: false,
      adminToken: '',
      adminPhotos: [],
      adminFilter: 'pending',
      adminFilters: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'all', label: 'All' }
      ],
      busyId: '',
      lightboxPhoto: null
    };
  },

  computed: {
    validEmail: function () {
      return /^\S+@\S+\.\S+$/.test(this.email);
    },
    canUpload: function () {
      return !this.uploading && this.selectedFiles.length > 0 && this.validEmail && this.termsAccepted;
    },
    pendingPhotos: function () {
      return this.adminPhotos.filter(function (photo) { return photo.status === 'pending'; });
    },
    approvedAdminPhotos: function () {
      return this.adminPhotos.filter(function (photo) { return photo.status === 'approved'; });
    },
    filteredAdminPhotos: function () {
      var filter = this.adminFilter;
      if (filter === 'all') return this.adminPhotos;
      return this.adminPhotos.filter(function (photo) { return photo.status === filter; });
    }
  },

  mounted: function () {
    document.addEventListener('keydown', this.handleKeydown);
    this.initialise();
  },

  beforeDestroy: function () {
    document.removeEventListener('keydown', this.handleKeydown);
    this.releasePreviews();
  },

  methods: {
    initialise: async function () {
      this.capturePrivateLink();
      await Promise.all([this.loadGallery(), this.restoreAdminSession(), this.loadOwnPhotos()]);
    },

    capturePrivateLink: function () {
      var url = new URL(window.location.href);
      var fragment = new URLSearchParams(url.hash.replace(/^#/, ''));
      var adminToken = url.searchParams.get('admin') || fragment.get('admin');
      var ownerToken = fragment.get('my');

      if (ownerToken) this.rememberToken(ownerToken);
      if (adminToken) {
        this.adminToken = adminToken;
        window.sessionStorage.setItem('debAdminBootstrap', adminToken);
        window.sessionStorage.setItem('debAdminToken', adminToken);
      }

      if (adminToken || ownerToken) {
        url.searchParams.delete('admin');
        if (fragment.has('admin') || fragment.has('my')) url.hash = '';
        window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
      }
    },

    restoreAdminSession: async function () {
      var token = window.sessionStorage.getItem('debAdminToken') ||
        window.sessionStorage.getItem('debAdminBootstrap');
      try {
        if (token) {
          this.adminToken = token;
          var login = await this.postJson('admin_login', { token: token });
          this.adminMode = !!login.authenticated;
          window.sessionStorage.removeItem('debAdminBootstrap');
        } else {
          var session = await this.getJson('admin_session');
          this.adminMode = !!session.authenticated;
        }
        if (this.adminMode) await this.loadAdminPhotos();
      } catch (error) {
        window.sessionStorage.removeItem('debAdminBootstrap');
        window.sessionStorage.removeItem('debAdminToken');
        this.adminToken = '';
        this.notice = { type: 'error', message: error.message };
      }
    },

    adminLogout: async function () {
      try {
        await this.postJson('admin_logout', {});
      } catch (error) {
        // Clear the local view even if the session has already expired.
      }
      this.adminMode = false;
      this.adminToken = '';
      this.adminPhotos = [];
      window.sessionStorage.removeItem('debAdminToken');
    },

    loadGallery: async function () {
      this.galleryLoading = true;
      try {
        var result = await this.getJson('list');
        this.galleryPhotos = result.photos || [];
      } catch (error) {
        this.notice = { type: 'error', message: error.message };
      } finally {
        this.galleryLoading = false;
      }
    },

    loadAdminPhotos: async function () {
      var result = await this.getJson('admin_list');
      this.adminPhotos = result.photos || [];
    },

    loadOwnPhotos: async function () {
      var tokens = this.savedTokens();
      if (!tokens.length) {
        this.ownPhotos = [];
        return;
      }

      var requests = tokens.map(function (token) {
        return this.postJson('my_uploads', { delete_token: token })
          .then(function (result) {
            return (result.photos || []).map(function (photo) {
              return Object.assign({}, photo, { token: token });
            });
          })
          .catch(function () { return []; });
      }, this);

      var groups = await Promise.all(requests);
      this.ownPhotos = [].concat.apply([], groups);
    },

    managePhotosByEmail: async function () {
      if (!this.validEmail || this.managing) return;

      this.managing = true;
      this.managementToken = '';
      this.ownPhotos = [];
      this.notice = { type: '', message: '' };
      try {
        var result = await this.postJson('manage_by_email', { email: this.email });
        this.managementToken = result.management_token || '';
        this.ownPhotos = (result.photos || []).map(function (photo) {
          return Object.assign({}, photo, { managementToken: result.management_token });
        });

        if (this.ownPhotos.length) {
          this.notice = {
            type: 'success',
            message: 'Your uploads are shown below. This private management session remains open for 30 minutes.'
          };
          this.$nextTick(function () {
            if (this.$refs.ownPhotos) {
              this.$refs.ownPhotos.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }.bind(this));
        } else {
          this.notice = {
            type: 'success',
            message: 'No active uploads were found. Make sure you entered the same email used when uploading.'
          };
        }
      } catch (error) {
        this.notice = { type: 'error', message: error.message };
      } finally {
        this.managing = false;
      }
    },

    handleFiles: function (event) {
      var files = Array.prototype.slice.call(event.target.files || []);
      this.releasePreviews();
      this.notice = { type: '', message: '' };

      if (files.length > 10) {
        this.notice = { type: 'error', message: 'Please choose no more than 10 photographs at a time.' };
        files = files.slice(0, 10);
      }

      var tooLarge = files.find(function (file) { return file.size > 15 * 1024 * 1024; });
      if (tooLarge) {
        this.notice = { type: 'error', message: tooLarge.name + ' is larger than 15 MB.' };
        files = files.filter(function (file) { return file.size <= 15 * 1024 * 1024; });
      }

      this.selectedFiles = files.map(function (file, index) {
        return {
          file: file,
          key: file.name + '-' + file.lastModified + '-' + index,
          name: file.name,
          size: file.size,
          preview: URL.createObjectURL(file)
        };
      });
      event.target.value = '';
    },

    removeSelected: function (index) {
      URL.revokeObjectURL(this.selectedFiles[index].preview);
      this.selectedFiles.splice(index, 1);
    },

    releasePreviews: function () {
      this.selectedFiles.forEach(function (item) { URL.revokeObjectURL(item.preview); });
    },

    submitUpload: function () {
      if (!this.canUpload) return;

      var form = new FormData();
      form.append('action', 'upload');
      form.append('email', this.email);
      form.append('terms_accepted', '1');
      this.selectedFiles.forEach(function (item) { form.append('photos[]', item.file, item.name); });

      this.uploading = true;
      this.uploadProgress = 0;
      this.notice = { type: '', message: '' };

      var request = new XMLHttpRequest();
      var uploadSeparator = this.apiUrl.indexOf('?') === -1 ? '?' : '&';
      request.open('POST', this.apiUrl + uploadSeparator + 'action=upload', true);
      request.withCredentials = false;
      request.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      request.upload.onprogress = function (event) {
        if (event.lengthComputable) this.uploadProgress = Math.round((event.loaded / event.total) * 100);
      }.bind(this);
      request.onload = function () {
        var response;
        try {
          response = JSON.parse(request.responseText || '{}');
        } catch (error) {
          response = { success: false, message: 'The server returned an unreadable response.' };
        }

        if (request.status >= 200 && request.status < 300 && response.success) {
          this.rememberToken(response.delete_token);
          this.notice = {
            type: 'success',
            message: response.message + ' Use “Manage my photos” with this email whenever you want to remove an upload.'
          };
          this.releasePreviews();
          this.selectedFiles = [];
          this.termsAccepted = false;
          this.loadOwnPhotos();
          if (this.adminMode) this.loadAdminPhotos();
        } else {
          this.notice = { type: 'error', message: response.message || 'The upload could not be completed.' };
        }
        this.uploading = false;
      }.bind(this);
      request.onerror = function () {
        this.uploading = false;
        this.notice = { type: 'error', message: 'The upload lost its connection. Please try again.' };
      }.bind(this);
      request.send(form);
    },

    deleteOwn: async function (photo) {
      if (!window.confirm('Permanently delete this photograph? This cannot be undone.')) return;
      this.busyId = photo.id;
      try {
        if (photo.managementToken) {
          await this.postJson('delete_by_email', {
            id: photo.id,
            management_token: photo.managementToken
          });
          this.ownPhotos = this.ownPhotos.filter(function (item) { return item.id !== photo.id; });
          await this.loadGallery();
        } else {
          await this.postJson('delete_own', { id: photo.id, delete_token: photo.token });
          await Promise.all([this.loadOwnPhotos(), this.loadGallery()]);
        }
        this.notice = { type: 'success', message: 'Your photograph has been deleted.' };
      } catch (error) {
        this.notice = { type: 'error', message: error.message };
      } finally {
        this.busyId = '';
      }
    },

    moderate: async function (photo, status) {
      this.busyId = photo.id;
      try {
        await this.postJson('moderate', { id: photo.id, status: status });
        await Promise.all([this.loadAdminPhotos(), this.loadGallery()]);
      } catch (error) {
        this.notice = { type: 'error', message: error.message };
      } finally {
        this.busyId = '';
      }
    },

    deleteAdmin: async function (photo) {
      if (!window.confirm('Permanently delete this photograph and its files?')) return;
      this.busyId = photo.id;
      try {
        await this.postJson('delete_admin', { id: photo.id });
        await Promise.all([this.loadAdminPhotos(), this.loadGallery(), this.loadOwnPhotos()]);
      } catch (error) {
        this.notice = { type: 'error', message: error.message };
      } finally {
        this.busyId = '';
      }
    },

    getJson: async function (action) {
      var separator = this.apiUrl.indexOf('?') === -1 ? '?' : '&';
      var headers = { 'Accept': 'application/json' };
      if (this.adminToken) headers['X-Admin-Token'] = this.adminToken;
      var response = await fetch(this.apiUrl + separator + 'action=' + encodeURIComponent(action), {
        credentials: 'same-origin',
        headers: headers
      });
      return this.parseResponse(response);
    },

    postJson: async function (action, payload) {
      var headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      if (this.adminToken) headers['X-Admin-Token'] = this.adminToken;
      var response = await fetch(this.apiUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers,
        body: JSON.stringify(Object.assign({ action: action }, payload || {}))
      });
      return this.parseResponse(response);
    },

    parseResponse: async function (response) {
      var result;
      try {
        result = await response.json();
      } catch (error) {
        throw new Error('The gallery server returned an unreadable response.');
      }
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'The gallery request failed.');
      }
      return result;
    },

    rememberToken: function (token) {
      if (!token) return;
      var tokens = this.savedTokens();
      if (tokens.indexOf(token) === -1) tokens.push(token);
      window.localStorage.setItem('debDeleteTokens', JSON.stringify(tokens.slice(-20)));
    },

    savedTokens: function () {
      try {
        var value = JSON.parse(window.localStorage.getItem('debDeleteTokens') || '[]');
        return Array.isArray(value) ? value.filter(function (token) { return typeof token === 'string'; }) : [];
      } catch (error) {
        return [];
      }
    },

    adminCount: function (status) {
      if (status === 'all') return this.adminPhotos.length;
      return this.adminPhotos.filter(function (photo) { return photo.status === status; }).length;
    },

    statusLabel: function (status) {
      return {
        pending: 'Awaiting review',
        approved: 'Published',
        rejected: 'Not published'
      }[status] || status;
    },

    formatFileSize: function (bytes) {
      if (bytes < 1024 * 1024) return Math.max(1, Math.round(bytes / 1024)) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    openLightbox: function (photo) {
      this.lightboxPhoto = photo;
      document.documentElement.classList.add('deb-no-scroll');
    },

    closeLightbox: function () {
      this.lightboxPhoto = null;
      document.documentElement.classList.remove('deb-no-scroll');
    },

    handleKeydown: function (event) {
      if (event.key === 'Escape') this.closeLightbox();
    }
  }
};
</script>

<style scoped>
.deb-page {
  background: #fff;
  color: #000;
  min-height: 100vh;
  font-family: Raleway-Regular, Arial, Helvetica, sans-serif;
  font-size: 16px;
}

.deb-page *,
.deb-page *::before,
.deb-page *::after {
  box-sizing: border-box;
}

.deb-page button,
.deb-page input {
  font-family: inherit;
}

.deb-logo-bar {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 96px;
  padding: 18px;
  position: relative;
}

.deb-logo-bar img {
  display: block;
  height: auto;
  width: 150px;
}

.deb-logo-bar > strong {
  color: #255699;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: 18px;
}

.deb-banner {
  background: #255699;
  color: #fff;
  padding: 30px 0 32px;
}

.deb-banner h1 {
  color: #fff;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: clamp(32px, 4vw, 48px);
  line-height: 1.1;
  margin: 0 0 8px;
  text-transform: uppercase;
}

.deb-banner__venue {
  color: #fff;
  font-family: Raleway-Regular, Arial, Helvetica, sans-serif;
  font-size: 17px;
  margin: 0 0 22px;
}

.deb-event-details {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.deb-event-details span {
  border-left: 1px solid rgba(255, 255, 255, .4);
  color: #fff;
  font-size: 12px;
  padding: 0 18px;
  text-transform: uppercase;
}

.deb-event-details span:first-child {
  border-left: 0;
}

.deb-event-details strong {
  color: #fff;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
}

.deb-upload-section,
.deb-own-section,
.deb-admin-section,
.deb-gallery-section {
  padding: 64px 0;
}

.deb-upload-section,
.deb-gallery-section {
  background: #fff;
}

.deb-own-section {
  background: #f1f1f1;
}

.deb-admin-section {
  background: #e9eef5;
  border-top: 5px solid #255699;
}

.deb-section-heading {
  margin-bottom: 30px;
}

.deb-section-heading h2,
.deb-admin-heading h2 {
  color: #255699;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: 28px;
  margin: 0 0 8px;
  text-transform: uppercase;
}

.deb-section-heading p {
  color: #000;
  margin: 0;
}

.deb-instructions {
  border-left: 3px solid #255699;
  margin: 0 0 20px;
  padding: 2px 0 2px 16px;
}

.deb-instructions p {
  color: #555;
  font-size: 12px;
  margin: 0 0 5px;
}

.deb-instructions p:last-child {
  margin-bottom: 0;
}

.deb-instructions strong {
  color: #255699;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
}

.deb-upload-panel {
  background: #e9eef5;
  border-radius: 15px;
  padding: 34px;
}

.deb-upload-panel .form-label {
  color: #255699;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: 13px;
  text-transform: uppercase;
}

.deb-upload-panel .form-control {
  background: #fff;
  border: 1px solid #d9dadd;
  border-radius: 30px;
  min-height: 48px;
  padding: 10px 18px;
}

.deb-upload-panel .form-control:focus {
  border-color: #255699;
  box-shadow: 0 0 0 .2rem rgba(37, 86, 153, .15);
}

.deb-email-row {
  align-items: stretch;
  display: flex;
  gap: 10px;
}

.deb-email-row .form-control {
  flex: 1;
  min-width: 0;
}

.deb-manage-button {
  background: #255699;
  border: 0;
  border-radius: 30px;
  color: #fff;
  flex: 0 0 auto;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: 11px;
  padding: 10px 20px;
  text-transform: uppercase;
  white-space: nowrap;
}

.deb-manage-button:hover:not(:disabled) {
  background: #12305b;
  color: #fff;
}

.deb-manage-button:disabled {
  opacity: .5;
}

.deb-upload-panel small {
  color: #555;
  display: block;
  font-size: 11px;
  margin-top: 7px;
}

.deb-file-input {
  height: 1px;
  margin: -1px;
  overflow: hidden;
  position: absolute;
  width: 1px;
  clip: rect(0 0 0 0);
}

.deb-file-picker {
  align-items: center;
  background: #fff;
  border: 1px dashed #a8bbd6;
  border-radius: 15px;
  color: #255699;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 150px;
  padding: 24px;
  width: 100%;
}

.deb-file-picker:hover {
  border-color: #255699;
}

.deb-file-picker:disabled {
  cursor: wait;
  opacity: .6;
}

.deb-file-picker__icon {
  font-size: 34px;
  line-height: 1;
  margin-bottom: 6px;
}

.deb-file-picker strong {
  color: #255699;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  text-transform: uppercase;
}

.deb-file-picker small {
  margin-top: 4px;
}

.deb-selected-list {
  display: grid;
  gap: 8px;
  margin-top: 14px;
}

.deb-selected-item {
  align-items: center;
  background: #fff;
  border-radius: 10px;
  display: grid;
  gap: 12px;
  grid-template-columns: 56px minmax(0, 1fr) 32px;
  padding: 8px;
}

.deb-selected-item img {
  border-radius: 6px;
  height: 48px;
  object-fit: cover;
  width: 56px;
}

.deb-selected-item div {
  min-width: 0;
}

.deb-selected-item strong,
.deb-selected-item small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.deb-selected-item strong {
  color: #255699;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: 12px;
}

.deb-selected-item small {
  margin: 0;
}

.deb-selected-item button {
  background: transparent;
  border: 0;
  color: #255699;
  cursor: pointer;
  font-size: 22px;
}

.deb-progress {
  align-items: center;
  display: flex;
  gap: 12px;
  margin-top: 16px;
}

.deb-progress > div {
  background: #d9dadd;
  border-radius: 4px;
  flex: 1;
  height: 5px;
  overflow: hidden;
}

.deb-progress span {
  background: #255699;
  display: block;
  height: 100%;
}

.deb-progress small {
  margin: 0;
}

.deb-terms-check {
  align-items: flex-start;
  display: flex;
  gap: 9px;
  margin: 18px 0 8px;
}

.deb-terms-check input {
  accent-color: #255699;
  flex: 0 0 auto;
  margin-top: 3px;
}

.deb-terms-check label {
  color: #555;
  font-size: 11px;
  line-height: 1.5;
  margin: 0;
}

.deb-terms {
  border-top: 1px solid #d9dadd;
  color: #555;
  font-size: 11px;
  margin-bottom: 20px;
  padding-top: 9px;
}

.deb-terms summary {
  color: #255699;
  cursor: pointer;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
}

.deb-terms ul {
  line-height: 1.55;
  margin: 10px 0 0;
  padding-left: 18px;
}

.deb-terms li {
  margin-bottom: 6px;
}

.deb-submit-button {
  background: #255699;
  border: 0;
  border-radius: 30px;
  color: #fff;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  padding: 10px 40px;
  text-transform: uppercase;
}

.deb-submit-button:hover:not(:disabled) {
  background: #12305b;
  color: #fff;
}

.deb-submit-button:disabled {
  opacity: .5;
}

.deb-notice {
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  margin-top: 18px;
  padding: 16px 20px;
}

.deb-notice strong {
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
}

.deb-notice--success {
  background: #e9eef5;
  color: #12305b;
}

.deb-notice--error {
  background: #f1f1f1;
  border-left: 4px solid #255699;
  color: #12305b;
}

.deb-notice a {
  color: inherit;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  margin-top: 5px;
}

.deb-photo-card {
  background: #fff;
  border-radius: 15px;
  height: 100%;
  overflow: hidden;
}

.deb-photo-card > img,
.deb-photo-card__image img {
  aspect-ratio: 4 / 3;
  display: block;
  object-fit: cover;
  width: 100%;
}

.deb-photo-card > div {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 13px;
}

.deb-photo-card > div > button {
  background: transparent;
  border: 0;
  color: #255699;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: 11px;
  padding: 0;
  text-decoration: underline;
}

.deb-photo-card__image {
  background: #fff;
  border: 0;
  cursor: zoom-in;
  display: block;
  padding: 0;
  width: 100%;
}

.deb-status {
  border-radius: 20px;
  display: inline-block;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: 9px;
  padding: 5px 9px;
  text-transform: uppercase;
}

.deb-status--pending {
  background: #e9eef5;
  color: #255699;
}

.deb-status--approved {
  background: #255699;
  color: #fff;
}

.deb-status--rejected {
  background: #d9dadd;
  color: #12305b;
}

.deb-admin-heading {
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
}

.deb-admin-heading > div > span {
  color: #255699;
  font-size: 11px;
  text-transform: uppercase;
}

.deb-logout {
  background: transparent;
  border: 0;
  color: #255699;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: 11px;
  text-decoration: underline;
  text-transform: uppercase;
}

.deb-admin-filters {
  border-bottom: 1px solid #a8bbd6;
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  overflow-x: auto;
}

.deb-admin-filters button {
  background: transparent;
  border: 0;
  border-bottom: 3px solid transparent;
  color: #555;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: 11px;
  padding: 8px 0;
  text-transform: uppercase;
  white-space: nowrap;
}

.deb-admin-filters button.active {
  border-bottom-color: #255699;
  color: #255699;
}

.deb-admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.deb-admin-actions button {
  background: #255699;
  border: 0;
  border-radius: 20px;
  color: #fff;
  font-family: Raleway-Bold, Arial, Helvetica, sans-serif;
  font-size: 9px;
  padding: 6px 10px;
  text-transform: uppercase;
}

.deb-admin-actions button.delete {
  background: #12305b;
}

.deb-admin-actions button:disabled {
  opacity: .45;
}

.deb-empty {
  background: rgba(255, 255, 255, .55);
  border-radius: 10px;
  color: #555;
  padding: 24px;
  text-align: center;
}

.deb-gallery-section {
  background: #fff;
}

.deb-gallery-grid {
  column-count: 4;
  column-gap: 12px;
}

.deb-gallery-grid button {
  background: #f1f1f1;
  border: 0;
  border-radius: 10px;
  break-inside: avoid;
  cursor: zoom-in;
  display: block;
  margin: 0 0 12px;
  overflow: hidden;
  padding: 0;
  width: 100%;
}

.deb-gallery-grid img {
  display: block;
  height: auto;
  width: 100%;
}

.deb-loading-tile {
  animation: deb-pulse 1s infinite alternate;
  background: #f1f1f1;
  border-radius: 10px;
  display: block;
  min-height: 220px;
}

@keyframes deb-pulse {
  to { opacity: .45; }
}

.deb-sponsors {
  background: #12305b;
  color: #d0d2d6;
  font-size: 11px;
  padding: 24px 15px;
  text-align: center;
  text-transform: uppercase;
}

.deb-lightbox {
  align-items: center;
  background: rgba(18, 48, 91, .96);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 4vw;
  position: fixed;
  z-index: 99999;
}

.deb-lightbox > button {
  background: transparent;
  border: 0;
  color: #fff;
  font-size: 40px;
  position: absolute;
  right: 22px;
  top: 14px;
}

.deb-lightbox img {
  max-height: 90vh;
  max-width: 94vw;
  object-fit: contain;
}

@media (max-width: 991px) {
  .deb-gallery-grid {
    column-count: 3;
  }
}

@media (max-width: 767px) {
  .deb-logo-bar {
    min-height: 80px;
    padding: 14px;
  }

  .deb-logo-bar img {
    width: 130px;
  }

  .deb-banner {
    padding: 24px 0;
  }

  .deb-banner h1 {
    font-size: 30px;
  }

  .deb-banner__venue {
    font-size: 14px;
    margin-bottom: 15px;
  }

  .deb-event-details {
    gap: 6px 0;
  }

  .deb-event-details span {
    border-left: 0;
    font-size: 10px;
    padding: 0 8px;
    width: 50%;
  }

  .deb-upload-section,
  .deb-own-section,
  .deb-admin-section,
  .deb-gallery-section {
    padding: 44px 0;
  }

  .deb-section-heading h2,
  .deb-admin-heading h2 {
    font-size: 23px;
  }

  .deb-upload-panel {
    border-radius: 10px;
    padding: 22px 16px;
  }

  .deb-email-row {
    flex-direction: column;
  }

  .deb-manage-button {
    min-height: 46px;
    width: 100%;
  }

  .deb-gallery-grid {
    column-count: 2;
    column-gap: 7px;
  }

  .deb-gallery-grid button {
    border-radius: 7px;
    margin-bottom: 7px;
  }
}
</style>

<style>
html.deb-no-scroll {
  overflow: hidden;
}
</style>
